import React, { useState, useEffect, useRef } from 'react';
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Loader2 } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { authService } from '@/integrations/firebase/authService';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

const countryCodes = [
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', name: 'France', flag: '🇫🇷' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+7', name: 'Russia', flag: '🇷🇺' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: '+971', name: 'UAE', flag: '🇦🇪' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+60', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+234', name: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', name: 'South Africa', flag: '🇿🇦' },
];

const PhoneAuth = () => {
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sentOtp, setSentOtp] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const navigate = useNavigate();
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  // Initialize recaptcha when component mounts
  useEffect(() => {
    const recaptchaId = 'recaptcha-container';
    
    // Cleanup any existing recaptcha instances first
    authService.cleanupRecaptcha();
    
    // Create recaptcha container if it doesn't exist
    if (recaptchaContainerRef.current) {
      // Clear any existing content
      while (recaptchaContainerRef.current.firstChild) {
        recaptchaContainerRef.current.removeChild(recaptchaContainerRef.current.firstChild);
      }
      
      // Create a new container
      const recaptchaElement = document.createElement('div');
      recaptchaElement.id = recaptchaId;
      recaptchaElement.className = 'firebase-recaptcha';
      recaptchaContainerRef.current.appendChild(recaptchaElement);
      
      // Wait for DOM to update before initializing recaptcha
      setTimeout(() => {
        try {
          authService.initRecaptcha(recaptchaId);
          console.log('Recaptcha initialized successfully');
        } catch (error) {
          console.error('Failed to initialize recaptcha:', error);
          toast.error('Failed to initialize phone authentication');
        }
      }, 500);
    }
    
    // Cleanup recaptcha on unmount
    return () => {
      authService.cleanupRecaptcha();
    };
  }, []);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phone || phone.trim().length < 5) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const fullPhoneNumber = `${countryCode}${phone.replace(/\D/g, '')}`;
      
      // Send OTP using Firebase auth service
      const verificationId = await authService.sendOtp(fullPhoneNumber);
      setVerificationId(verificationId);
      
      setSentOtp(true);
      toast.success("OTP sent to your phone number");
    } catch (error: any) {
      toast.error(error.message || "Failed to send OTP");
      // Re-initialize recaptcha on error
      if (recaptchaContainerRef.current) {
        authService.cleanupRecaptcha();
        setTimeout(() => {
          const recaptchaId = 'recaptcha-container';
          const recaptchaElement = document.createElement('div');
          recaptchaElement.id = recaptchaId;
          recaptchaElement.className = 'firebase-recaptcha';
          recaptchaContainerRef.current?.appendChild(recaptchaElement);
          authService.initRecaptcha(recaptchaId);
        }, 1000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createInitialProfile = async (userId: string, phoneNumber: string) => {
    try {
      // This function would create the initial profile in your database
      // For now, we'll just log the user ID and phone number
      console.log('Creating initial profile for user:', userId, 'with phone:', phoneNumber);
      
      // TODO: Implement profile creation in your database
      // This would replace the Supabase profile creation logic
      
      return true;
    } catch (error: any) {
      console.error('Error creating initial profile:', error);
      throw error;
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!otp || otp.trim().length < 4) {
      toast.error("Please enter a valid OTP");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Verify OTP using Firebase auth service
      const user = await authService.verifyOtp(verificationId, otp);

      if (user) {
        // Create initial profile after successful authentication
        const fullPhoneNumber = `${countryCode}${phone.replace(/\D/g, '')}`;
        await createInitialProfile(user.uid, fullPhoneNumber);
      }

      toast.success("Phone number verified successfully");
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message || "Failed to verify OTP");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-white rounded-xl shadow-sm">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-bold">Sign in with your phone</h2>
        <p className="text-muted-foreground mt-2">
          {!sentOtp 
            ? "We'll send you a one-time code to verify your phone number" 
            : "Enter the verification code sent to your phone"}
        </p>
      </div>

      {/* Hidden recaptcha container */}
      <div ref={recaptchaContainerRef} className="hidden"></div>

      {!sentOtp ? (
        <form onSubmit={handleSendOtp} className="space-y-4">
          <div className="space-y-2">
            <div className="flex">
              <Select value={countryCode} onValueChange={setCountryCode}>
                <SelectTrigger className="w-[110px] rounded-r-none border-r-0">
                  <SelectValue placeholder="+91" />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      <span className="flex items-center gap-2">
                        <span>{country.flag}</span>
                        <span>{country.code}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="tel"
                placeholder="Phone number without code"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded-l-none"
                required
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter your number without country code (e.g., 9876543210)
            </p>
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending code...
              </>
            ) : (
              "Send verification code"
            )}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              required
            />
            <div className="flex justify-between">
              <p className="text-xs text-muted-foreground">
                Enter the code sent to your phone
              </p>
              <button 
                type="button"
                onClick={() => setSentOtp(false)}
                className="text-xs text-primary hover:underline"
              >
                Change phone number
              </button>
            </div>
          </div>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify & Sign in"
            )}
          </Button>
        </form>
      )}
    </div>
  );
};

export default PhoneAuth;
